import { LoaderCircle } from 'lucide-react'
import { useListIncidentDraftsQuery, type WorkScope } from '@axilon-apps/domains-ontology-ui-state'
import { occurrenceHref } from '../../lib/component-links'
import { WorkError } from './OccurrenceTasks'
import { ProblemSummaryRow } from './ProblemSummaryRow'

/** Staged writes emit no change signal, so drafts refresh on a short poll. */
export const DRAFT_POLL_MS = 10_000

/**
 * Problems a running investigation has recorded but not published yet,
 * usually because the run created a new Problem concept that publishes when
 * the run finishes. Each links to its own page, like a published problem.
 */
export function DraftProblems({ scope, runId }: { scope: WorkScope; runId?: string }) {
  const query = useListIncidentDraftsQuery(
    { ...scope, ...(runId ? { runId } : {}) },
    { pollingInterval: DRAFT_POLL_MS, refetchOnMountOrArgChange: true }
  )
  if (query.isError)
    return (
      <WorkError
        message="Couldn't load problems that are still being investigated."
        retry={() => void query.refetch()}
      />
    )
  const drafts = query.currentData ?? []
  if (!drafts.length) return null
  return (
    <section aria-label="Being investigated" className="flex min-w-0 flex-col gap-2">
      <header className="flex items-center gap-2 px-1">
        <LoaderCircle
          size={14}
          className="animate-spin text-brand motion-reduce:animate-none"
          aria-hidden
        />
        <h2 className="m-0 text-13 font-medium text-ink-2">Being investigated</h2>
        <span className="text-12 tabular-nums text-ink-3">{drafts.length}</span>
        <span className="text-12 text-ink-3">· published when the run finishes</span>
      </header>
      <ul className="m-0 flex list-none flex-col divide-y divide-line/70 overflow-hidden rounded-lg border border-dashed border-line-2 bg-card p-0">
        {drafts.map((draft) => (
          <ProblemSummaryRow
            key={`${draft.run_id}:${draft.occurrence.id}`}
            occurrence={draft.occurrence}
            href={occurrenceHref(draft.occurrence.id, draft.run_id)}
            draft
          />
        ))}
      </ul>
    </section>
  )
}
